import type { Row } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { normalizeName } from "../../../shared/normalizeName";
import { queries } from "../../../shared/queries";
import { useMouseDownOutside } from "../../hooks/useMouseDownOutside";
import { useQuery } from "../../hooks/useQuery";
import { zIndexes } from "../../lib/zIndexes";
import { AnchoredPopover } from "../AnchoredPopover";
import { marchingDashes } from "../AttachmentPreviews";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { useGetDemo } from "../GetDemo/GetDemoFlow";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { HeroScene } from "./HeroScene";

const OPEN_DELAY = 40;
const HIDE_DELAY = 300;
const DASH_CYCLE_MS = 2000;

// Avatar only reads the identity fields; a full fake row would break on every
// schema change.
const EXPERTS = [
  {
    id: "duncan-hollander",
    name: normalizeName("Duncan Hollander"),
    email: "duncan@3b.example",
    image: "/assets/images/duncan-hollander.png",
    customAvatarKey: null,
  } as Row["user"],
  {
    id: "leah-ohara",
    name: normalizeName("Leah O’Hara"),
    email: "leah@3b.example",
    image: "/assets/images/leah-ohara.png",
    customAvatarKey: null,
  } as Row["user"],
];

const EXPERT_USER = EXPERTS[Math.floor(Math.random() * EXPERTS.length)]!;
const EXPERT_FIRST_NAME = EXPERT_USER.name.split(" ")[0] ?? EXPERT_USER.name;

const Wrap = styled("div")`
  position: relative;
`;

const CtaButton = styled(PressButton)`
  position: relative;
  height: 24px;
  align-items: center;
  &:hover [data-pill] {
    background: var(--orange-400);
  }
  & rect {
    stroke: var(--orange-600);
    animation: ${marchingDashes} ${DASH_CYCLE_MS}ms linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    & rect {
      animation: none;
    }
  }
`;

const DashOutline = styled("svg")`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
`;

const Popover = styled(AnchoredPopover)`
  z-index: ${zIndexes.dropdown};
`;

const Card = styled("div")`
  width: 320px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
`;

const Hero = styled("div")`
  position: relative;
  height: 112px;
  border-radius: 8px;
  background: var(--surface-secondary);
  overflow: hidden;
`;

const Stack = styled("div")`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  z-index: 3;
`;

const Front = styled("div")`
  position: relative;
  margin-left: -4px;
  display: flex;
  align-items: center;
`;

const TriggerStack = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
`;

const TriggerBack = styled("div")`
  position: relative;
  z-index: 1;
`;

const TriggerFront = styled("div")`
  position: relative;
  margin-left: -4px;
`;

// The inset surface ring carves the same breathing gap between the dashed
// border and the fill that the padded avatars have between ring and face.
const Pill = styled("span")`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 24px;
  margin-left: -10px;
  padding: 0 12px 0 18px;
  border-radius: 9999px;
  background: var(--orange-200);
  box-shadow: inset 0 0 0 2px var(--surface-secondary);
`;

const Content = styled("div")`
  padding-top: 16px;
  display: flex;
  flex-direction: column;
`;

const DemoButton = styled(Button)`
  width: 100%;
  justify-content: center;
  margin-top: 16px;
  border-radius: 9999px;
`;

export function TalkToExpertButton({ user }: { user: Row["user"] }) {
  const [tenant] = useQuery(queries.currentTenant());
  const zero = useZero();
  const { available, openDemo, modals } = useGetDemo({ user, zero });
  const [open, setOpen] = useState(false);
  // Phase-locked to the wall clock so the remount on every page navigation
  // (each page renders its own Backdrop) doesn't visibly reset the dashes.
  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  const dashPhase = useMemo(() => `${-(Date.now() % DASH_CYCLE_MS)}ms`, []);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const close = useCallback(() => setOpen(false), []);
  const [containerRef, portalRef] = useMouseDownOutside(close);

  const clearOpenTimer = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };
  const clearHideTimer = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const startHideTimer = () => {
    clearHideTimer();
    hideTimer.current = setTimeout(() => setOpen(false), HIDE_DELAY);
  };

  const handleTriggerEnter = () => {
    clearHideTimer();
    if (!open) {
      clearOpenTimer();
      openTimer.current = setTimeout(() => setOpen(true), OPEN_DELAY);
    }
  };
  const handleTriggerLeave = () => {
    clearOpenTimer();
    if (open) startHideTimer();
  };
  const toggle = () => {
    clearOpenTimer();
    clearHideTimer();
    setOpen((was) => !was);
  };

  useEffect(
    () => () => {
      clearOpenTimer();
      clearHideTimer();
    },
    []
  );

  if (!tenant?.isExploreSignup || !available) return null;

  return (
    <Wrap
      ref={containerRef}
      onMouseEnter={handleTriggerEnter}
      onMouseLeave={handleTriggerLeave}
    >
      <Popover
        isOpen={open}
        popoverRef={portalRef}
        direction="down"
        style={
          {
            top: "12px",
            left: "anchor(center)",
            translate: "-50% 0",
          } as React.CSSProperties
        }
        trigger={(props) => (
          <div {...props}>
            <CtaButton
              onPress={toggle}
              aria-expanded={open}
              onMouseDown={(e) => e.preventDefault()}
            >
              <TriggerStack
                style={
                  {
                    "--cell-bg": "var(--surface-secondary)",
                  } as React.CSSProperties
                }
              >
                <TriggerBack>
                  <Avatar
                    user={user}
                    size={20}
                    tooltip={false}
                    bare={false}
                    isPresent
                  />
                </TriggerBack>
                <TriggerFront>
                  <Avatar
                    user={EXPERT_USER}
                    size={20}
                    tooltip={false}
                    bare={false}
                    isPresent
                  />
                </TriggerFront>
              </TriggerStack>
              <Pill data-pill>
                <DashOutline aria-hidden>
                  {/* marchingDashes travels 10px per cycle, so the dash period
                      (3 + 2) must divide 10 to loop seamlessly. */}
                  <rect
                    x="0.5"
                    y="0.5"
                    width="calc(100% - 1px)"
                    height="calc(100% - 1px)"
                    rx="11.5"
                    fill="none"
                    strokeWidth="1"
                    strokeDasharray="3 2"
                    style={{ animationDelay: dashPhase }}
                  />
                </DashOutline>
                <Text variant="text-small-md" color="var(--orange-1000)">
                  Book a demo
                </Text>
              </Pill>
            </CtaButton>
          </div>
        )}
      >
        <Card onMouseEnter={clearHideTimer} onMouseLeave={startHideTimer}>
          <Hero>
            <HeroScene />
            <Stack
              style={
                {
                  "--cell-bg": "var(--surface-secondary)",
                } as React.CSSProperties
              }
            >
              <Avatar user={user} size={20} bare={false} isPresent />
              <Front>
                <Tooltip content={`${EXPERT_FIRST_NAME} from Tines`}>
                  <Avatar
                    user={EXPERT_USER}
                    size={20}
                    tooltip={false}
                    bare={false}
                    isPresent
                  />
                </Tooltip>
              </Front>
            </Stack>
          </Hero>
          <Content>
            <Text variant="text-regular-md">Build with an expert</Text>
            <Text
              variant="text-regular"
              color="var(--text-secondary)"
              style={{ marginTop: 4 }}
            >
              Get a live walkthrough of 3B, tailored to what your team wants to
              build or understand.
            </Text>
            <DemoButton
              variant="primary"
              size="l"
              onClick={() => {
                setOpen(false);
                openDemo();
              }}
            >
              Book a demo
            </DemoButton>
          </Content>
        </Card>
      </Popover>
      {modals}
    </Wrap>
  );
}
